import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Stack,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";

const statusColor = (status) => {
  switch (status) {
    case "new":
      return "error";
    case "contacted":
      return "warning";
    case "replied":
      return "primary";
    case "converted":
      return "success";
    default:
      return "default";
  }
};

const kindColor = (kind) => {
  switch (kind) {
    case "uncaught_exception":
      return "error";
    case "react_error":
      return "warning";
    case "user_visible_error":
      return "info";
    default:
      return "default";
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return value;
  }
};

const MobileAppIssues = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("new");
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (kindFilter) params.kind = kindFilter;
      if (platformFilter) params.platform = platformFilter;
      if (emailFilter.trim()) params.email = emailFilter.trim();
      const res = await api.get("admin/mobile-app-issues", { params });
      if (res.data?.success) setIssues(res.data.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load mobile app issues.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [statusFilter, kindFilter, platformFilter]);

  const kindOptions = useMemo(() => {
    const set = new Set(issues.map((row) => row.kind).filter(Boolean));
    return Array.from(set).sort();
  }, [issues]);

  const openRow = async (row) => {
    try {
      const res = await api.get(`admin/mobile-app-issues/${row.id}`);
      if (res.data?.success) {
        setSelected(res.data.data);
        setStatus(res.data.data.status ?? "new");
        setOpen(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load issue details.");
    }
  };

  const closeDialog = () => {
    setOpen(false);
    setSelected(null);
  };

  const handleSaveStatus = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      await api.patch(`admin/mobile-app-issues/${selected.id}/status`, { status });
      toast.success("Status updated.");
      await fetchIssues();
      const res = await api.get(`admin/mobile-app-issues/${selected.id}`);
      if (res.data?.success) setSelected(res.data.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update status.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Mobile App Issues
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Automatic crash and error reports from the student mobile app. Each row shows what went wrong,
        when it happened, and which user was affected.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="contacted">Reviewed</MenuItem>
              <MenuItem value="replied">Replied</MenuItem>
              <MenuItem value="converted">Resolved</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Issue type</InputLabel>
            <Select label="Issue type" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {kindOptions.map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {kind}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Platform</InputLabel>
            <Select label="Platform" value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ios">iOS</MenuItem>
              <MenuItem value="android">Android</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Filter by email"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <Button variant="outlined" onClick={fetchIssues}>
            Search
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : issues.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            No mobile app issues reported yet.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8f9ff" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Date &amp; time</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Phone OS</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Build</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Screen</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="caption">{formatDateTime(row.occurred_at || row.created_at)}</Typography>
                    </TableCell>
                    <TableCell>{row.platform_label || row.platform || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{row.os_version || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 140 }}>{row.device_model || "—"}</TableCell>
                    <TableCell>{row.app_version || "—"}</TableCell>
                    <TableCell>{row.build_number || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.kind_label || row.kind || "—"}
                        color={kindColor(row.kind)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>{row.screen || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap title={row.error_message || row.error_preview}>
                        {row.error_preview || row.error_message || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>{row.email || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => openRow(row)}>
                        View details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={open && !!selected} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Mobile app issue #{selected?.id}</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                <strong>Occurred:</strong> {formatDateTime(selected.occurred_at || selected.created_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Issue type:</strong> {selected.kind_label || selected.kind || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Screen:</strong> {selected.screen || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Platform:</strong> {selected.platform_label || selected.platform || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Phone OS:</strong> {selected.os_version || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Device:</strong> {selected.device_model || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>App version:</strong> {selected.app_version || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Build number:</strong> {selected.build_number || "—"}
              </Typography>
              {selected.bundle_id ? (
                <Typography variant="body2">
                  <strong>Bundle ID:</strong> {selected.bundle_id}
                </Typography>
              ) : null}
              {selected.build_summary ? (
                <Typography variant="body2">
                  <strong>Build:</strong> {selected.build_summary}
                </Typography>
              ) : null}
              <Typography variant="body2">
                <strong>Student name:</strong> {selected.name || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {selected.email || "—"}
              </Typography>
              {selected.mobile_user_id ? (
                <Typography variant="body2">
                  <strong>Mobile user ID:</strong> {selected.mobile_user_id}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                <strong>Error message:</strong>
                {"\n"}
                {selected.error_message || "—"}
              </Typography>
              {selected.stack_trace ? (
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Stack trace
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: "#f8fafc",
                      fontFamily: "monospace",
                      fontSize: 12,
                      whiteSpace: "pre-wrap",
                      maxHeight: 220,
                      overflow: "auto",
                    }}
                  >
                    {selected.stack_trace}
                  </Paper>
                </Box>
              ) : null}
              {selected.context_json ? (
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Device / context
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: "#f8fafc",
                      fontFamily: "monospace",
                      fontSize: 12,
                      whiteSpace: "pre-wrap",
                      maxHeight: 220,
                      overflow: "auto",
                    }}
                  >
                    {selected.context_json}
                  </Paper>
                </Box>
              ) : null}

              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="contacted">Reviewed</MenuItem>
                  <MenuItem value="replied">Replied</MenuItem>
                  <MenuItem value="converted">Resolved</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={handleSaveStatus} disabled={saving}>
                {saving ? "Saving…" : "Update status"}
              </Button>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileAppIssues;
